import { eventTypeStyle } from '@/lib/eventTypes'

/**
 * News: the categories, the reader's filter and how a news becomes text (see doc/news.md).
 *
 * An automatic news is a rule with its params, and the text comes from the translations
 * (`news.auto.<rule>`), so the wording can change without touching the data; a manual one carries its
 * own text. The filter is a preference of the person, not of the account, so it lives on the device.
 */
export const NEWS_CATEGORIES = ['release', 'custom', 'event', 'player']

const STORAGE_KEY = 'news.categories'

/**
 * Icon and colour of every category, for the news without an event type — those wear the icon of
 * their event type instead (see newsStyle).
 */
export const CATEGORY_STYLES = {
  release: { icon: 'mdi-rocket-launch-outline', color: 'deep-orange' },
  custom: { icon: 'mdi-bullhorn-outline', color: 'teal' },
  event: { icon: 'mdi-calendar-star', color: 'primary' },
  // The player news are the many ones: a muted colour keeps a long list from shouting.
  player: { icon: 'mdi-account-star-outline', color: 'blue-grey' },
}

/**
 * Icon and colour of the player rules (see doc/news.md): a leader and a place are not the same thing,
 * so they do not wear the same icon. A rule not listed here falls back to its category.
 */
export const RULE_STYLES = {
  leader_ranking: { icon: 'mdi-crown', color: 'blue-grey' },
  leader_ranking_since: { icon: 'mdi-crown', color: 'blue-grey' },
  leader_records: { icon: 'mdi-medal', color: 'blue-grey' },
  track_record: { icon: 'mdi-trophy', color: 'blue-grey' },
  track_top10: { icon: 'mdi-podium', color: 'blue-grey' },
  top10_since: { icon: 'mdi-podium', color: 'blue-grey' },
}

/**
 * Icon and colour of one news: the icon of its rule when it has one, the event's own when it has an
 * event type, the category's otherwise.
 */
export function newsStyle(news) {
  if (news.rule && RULE_STYLES[news.rule]) {
    return RULE_STYLES[news.rule]
  }

  if (news.params?.eventType) {
    return eventTypeStyle(news.params.eventType)
  }

  return CATEGORY_STYLES[news.category] ?? { icon: 'mdi-newspaper-variant-outline', color: 'grey' }
}

/** The categories the reader wants to see: all of them until some are turned off. */
export function enabledCategories() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    const known = Array.isArray(saved) ? saved.filter(category => NEWS_CATEGORIES.includes(category)) : []

    if (known.length) {
      return known
    }
  } catch {
    // An unreadable value is no filter at all.
  }

  return [...NEWS_CATEGORIES]
}

export function setEnabledCategories(categories) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories))
}

/** Automatic news carry a `rule` and no text; manual ones carry the text and no rule. */
export function isManual(news) {
  return !news.rule
}

/** The line a list shows: for an automatic news it is the whole text, a rule is one sentence. */
export function newsTitle(news, t) {
  return isManual(news) ? (news.title ?? '') : t(`news.auto.${news.rule}`, newsParams(news, t))
}

/** The params of the template, with the labels it shows already translated. */
export function newsParams(news, t) {
  const params = { ...(news.params ?? {}) }

  if (params.eventType) {
    params.eventTypeLabel = t(`eventTypes.${params.eventType}`)
  }
  if (params.trackKey) {
    params.trackLabel = t(`tracks.${params.trackKey}`)
  }
  if (params.days) {
    params.period = period(params.days, t)
  }

  return params
}

/** "30 days", "6 months", "1 year": the thresholds are day counts, the reader wants a length. */
function period(days, t) {
  if (days % 365 === 0) {
    return t('news.period.years', { count: days / 365 }, days / 365)
  }
  if (days % 30 === 0) {
    return t('news.period.months', { count: days / 30 }, days / 30)
  }

  return t('news.period.days', { count: days }, days)
}
